import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import logoPng from '@src/assets/voylogic-logo.png';
import type { TripProfile } from '@generated/graphql';
import type { ItineraryItem } from '@src/types/itinerary';
import styles from './Itinerary-pdf.styles';

function formatRange(p: TripProfile) {
  const s = p.startDate
    ? new Date(p.startDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : 'N/A';
  const e = p.endDate
    ? new Date(p.endDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : 'N/A';
  return `${s} to ${e}`;
}
function fmtDate(d?: string | null) {
  if (!d) return '';
  const t = new Date(d);
  return isNaN(t.getTime())
    ? ''
    : t.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
}

function InfoChip({ title, value }: { title: string; value?: string | number | null }) {
  return (
    <View style={styles.chip}>
      <Text style={styles.chipTitle}>{title}</Text>
      <Text>{value ?? 'N/A'}</Text>
    </View>
  );
}

function ItineraryPdf({
  tripProfile,
  itinerary = [],
}: {
  tripProfile: TripProfile;
  itinerary?: ItineraryItem[] | null;
}) {
  return (
    <Document>
      <Page size="LETTER" style={styles.page} wrap>
        {/* Brand header */}
        <View style={styles.brandHeader}>
          <Image src={logoPng} style={styles.brandLogo} />
        </View>

        {/* Title block */}
        <View style={styles.header}>
          <Text style={styles.h1}>{tripProfile.title}</Text>
          <Text style={styles.h2}>
            {tripProfile.destination}
            {tripProfile.country ? `, ${tripProfile.country}` : ''}
          </Text>
        </View>

        <View style={styles.row}>
          <Text style={styles.muted}>{formatRange(tripProfile)}</Text>
          <Text style={styles.muted}>Status: {tripProfile.status ?? 'N/A'}</Text>
        </View>

        <Text style={styles.sectionTitle}>Client Intake Details</Text>
        <View style={styles.grid}>
          <InfoChip title="Group Type" value={tripProfile.travelPartyType} />
          <InfoChip title="Number of Travelers" value={tripProfile.numberTravelers} />
          <InfoChip
            title="Budget"
            value={
              tripProfile.budgetLower != null && tripProfile.budgetUpper != null
                ? `$${new Intl.NumberFormat().format(tripProfile.budgetLower)} – $${new Intl.NumberFormat().format(tripProfile.budgetUpper)}`
                : 'N/A'
            }
          />
          <InfoChip title="Accommodations" value={tripProfile.accommodationType} />
          <InfoChip title="Occasion" value={tripProfile.occasionType} />
          <InfoChip title="Schedule Density" value={tripProfile.scheduleDensity} />
          <InfoChip title="Cultural Level" value={tripProfile.culturalComfortLevel} />
          <InfoChip title="Support Level" value={tripProfile.supportLevel} />
        </View>

        {Array.isArray(itinerary) && itinerary.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Trip Itinerary</Text>
            <View style={styles.table}>
              <View style={styles.trHead} fixed>
                <Text style={[styles.cell, styles.cDay]}>Day</Text>
                <Text style={[styles.cell, styles.cDate]}>Date</Text>
                <Text style={[styles.cell, styles.cAM]}>AM Activity</Text>
                <Text style={[styles.cell, styles.cPM]}>PM Activity</Text>
                <Text style={[styles.cell, styles.cDinner]}>Dinner</Text>
                <Text style={[styles.cell, styles.cHotel]}>Hotel</Text>
              </View>
              {itinerary.map((r, i) => (
                <View key={i} style={styles.tr} wrap>
                  <Text style={[styles.cell, styles.cDay]}>Day {r.day ?? ''}</Text>
                  <Text style={[styles.cell, styles.cDate]}>{fmtDate(r.date)}</Text>
                  <Text style={[styles.cell, styles.cAM]}>{r.amActivity ?? ''}</Text>
                  <Text style={[styles.cell, styles.cPM]}>{r.pmActivity ?? ''}</Text>
                  <Text style={[styles.cell, styles.cDinner]}>{r.dinner ?? ''}</Text>
                  <Text style={[styles.cell, styles.cHotel]}>{r.accommodations ?? ''}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
}

export default ItineraryPdf;
